'use client'

import React, { useMemo, useCallback, useState, useEffect } from 'react'
import { Header, HeaderDataDesktop } from '@quark/header'
import { Logout, Policy, Profile } from '@quark/icons'
import { Flex } from '@quark/layout'
import { Text } from '@quark/typography'
import { logoutUser } from '@services/loginService'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import { getModifiedSectionData, openLink } from '@factories/getGlobalHeaderData'
import { getUrlWithQueryParams } from '@utils/queryParamUtils'
import { Locale } from '@enums/strapiTypesAndOperators'
import { getLanguagePickerOptions } from '@utils/localisation/languagePickerOptions'
import logger from '@utils/logger'

interface DesktopHeaderProps {
  isLoggedIn: boolean
  userData?: any
  content?: any
  animate?: boolean
  ackoLogo?: string
  isSemPage?: boolean
  setFormSnippet?: (formSnippet: string) => void
  showLoginButton?: boolean
  selectedLanguage: Locale
  availableLanguages: Locale[]
  defaultLink?: string
}

const DesktopHeader: React.FC<DesktopHeaderProps> = ({
  isLoggedIn,
  userData,
  content,
  animate = false,
  ackoLogo = undefined,
  isSemPage = false,
  setFormSnippet,
  showLoginButton = true,
  selectedLanguage,
  availableLanguages,
  defaultLink = '/',
}) => {
  const config = useEnvVariablesClientConfig()
  const [logoHref, setLogoHref] = useState(defaultLink)

  useEffect(() => {
    setLogoHref(getUrlWithQueryParams(defaultLink))
  }, [])

  const handleLogout = useCallback(async () => {
    try {
      await logoutUser({
        env: config?.ACKO_ENV,
      })
    } catch (error) {
      logger.error('Logout failed', error)
    } finally {
      window.location.reload()
    }
  }, [config])

  const userSection = useMemo(() => {
    if (isLoggedIn) {
      return {
        name: (
          <Flex gap="spacing-12" alignItems="center" justifyContent="center">
            <Text fontStyle="um_text-xl/normal" color="base/primary" lineHeight="line-height-0">
              <Profile />
            </Text>
            <Text fontStyle="um_text-base/normal" color="base/primary">
              {userData?.name?.split(' ')[0]}
            </Text>
          </Flex>
        ),
        type: 'outlined-button' as const,
        dropDownType: 'contentWidth' as const,
        isExpandable: true,
        props: openLink('', [], ''),
        options: [
          { name: 'View Profile', icon: <Profile />, props: openLink('', [], '/profile') },
          { name: 'My Policies', icon: <Policy />, props: openLink('', [], '/myaccount') },
          {
            name: 'Logout',
            icon: <Logout />,
            props: {
              onClick: () => {
                void handleLogout()
              },
            },
          },
        ],
      }
    }

    return {
      name: 'Login',
      type: 'filled-button' as const,
      isExpandable: false,
      props: {
        onClick: () => {
          window.location.href = getUrlWithQueryParams('/authn/v1/signin')
        },
      },
    }
  }, [isLoggedIn, userData, handleLogout])

  const headerData: HeaderDataDesktop = useMemo(() => {
    const buildSection = (categories?: any[]) =>
      isSemPage ? [] : getModifiedSectionData(categories ?? [], isLoggedIn, setFormSnippet)

    const leftSection = buildSection(content?.leftSideCategories)
    const rightBase = buildSection(content?.rightSideCategories)

    const rightSection = [
      ...(isSemPage ? [] : showLoginButton ? [...rightBase, userSection] : rightBase),
      ...(availableLanguages.length > 1 ? [getLanguagePickerOptions(selectedLanguage, availableLanguages)] : []),
    ]

    return {
      logo: {
        darkUrl: isSemPage ? ackoLogo : content?.darkThemeLogoUrl,
        lightUrl: isSemPage ? ackoLogo : content?.lightThemeLogoUrl,
        props: { href: logoHref },
      },
      leftSection,
      rightSection,
    }
  }, [content, isLoggedIn, isSemPage, ackoLogo, setFormSnippet, showLoginButton, userSection, defaultLink, logoHref])
  return <Header headerData={headerData} animate={animate} haveBackground={!isSemPage} />
}

export default React.memo(DesktopHeader)
